import { useState } from "react";
import { ButtonBasic } from '@lk-gtcom/ecomlab-components'
import ImageViewer from "../../../components/ImageViewer/ImageViewer";
import customHeaders, { getSpace } from '../../../common/headers';
import useGeneralStore from "../../../store/general";
import { useResponseHandler } from "../../../common/auth";
import "./CardReturn.scss";

const REASON_MAP = {
    BAD_QUALITY: "Плохое качество",
    DOES_NOT_FIT: "Не подошёл",
    UNKNOWN: "Не указана",
};

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatPrice(price) {
    if (price == null || price === 0) return null;
    return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "RUB",
        maximumFractionDigits: 0,
    }).format(price);
}

function getReturnReason(reason) {
    if (!reason) return "Не указана";
    return REASON_MAP[reason] ?? reason;
}

export function CardReturn({ ret }) {
    const selectedSpace = useGeneralStore((state) => state.selectedSpace);
    const headers = { ...customHeaders, ...getSpace(selectedSpace) };
    const { handleStatusCode, handleTableResponse, handleRequestError } = useResponseHandler();

    const [selectedDecisions, setSelectedDecisions] = useState({});
    const [comments, setComments] = useState({});
    const [selectedReasons, setSelectedReasons] = useState({});
    const [compensationAmounts, setCompensationAmounts] = useState({});
    const [submittedItems, setSubmittedItems] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const availableDecisions = ret?.available_decisions ?? [];
    const needsDecision = availableDecisions.length > 0;
    const chosenDecision = (itemId) => {
        const selected = selectedDecisions[itemId];
        if (!selected) return null;
        return availableDecisions.find((d) => d?.code === selected);
    };

    const decisionStateModifier = needsDecision ? "warning" : "success";

    const getHasComment = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.comment_params !== null && decision?.comment_params !== undefined;
    };
    const getHasReason = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.reason_params !== null && decision?.reason_params !== undefined;
    };
    const getHasCompensation = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.compensation_params !== null && decision?.compensation_params !== undefined;
    };
    const getIsCommentRequired = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.comment_params?.is_required ?? false;
    };
    const getIsReasonRequired = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.reason_params?.is_required ?? false;
    };
    const getIsCompensationRequired = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.compensation_params?.is_required ?? false;
    };
    const getCommentTemplates = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.comment_params?.templates ?? [];
    };
    const getAvailableReasons = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.reason_params?.available_reasons ?? [];
    };
    const getCompensationMax = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.compensation_params?.max_amount ?? 0;
    };
    const getCompensationMin = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.compensation_params?.min_amount ?? 0;
    };
    const getCompensationMaxPercent = (itemId) => {
        const decision = chosenDecision(itemId);
        return decision?.compensation_params?.max_percent ?? 100;
    };

    function handleTemplateSelect(itemId, value) {
        setComments(prev => ({ ...prev, [itemId]: value }));
    }

    const isValidCompensation = (itemId) => {
        if (!getHasCompensation(itemId)) return true;
        const required = getIsCompensationRequired(itemId);
        const amount = compensationAmounts[itemId];
        const min = getCompensationMin(itemId);
        const max = getCompensationMax(itemId);

        if (required && !amount) return false;
        if (amount) {
            const numAmount = parseFloat(amount);
            if (isNaN(numAmount)) return false;
            if (min > 0 && numAmount < min) return false;
            if (max > 0 && numAmount > max) return false;
        }
        return true;
    };

    const isFormValid = (itemId) => {
        const selected = selectedDecisions[itemId];
        if (!selected) return false;
        if (getHasComment(itemId) && getIsCommentRequired(itemId) && !comments[itemId]?.trim()) return false;
        if (getHasReason(itemId) && getIsReasonRequired(itemId) && !selectedReasons[itemId]) return false;
        if (getHasCompensation(itemId) && !isValidCompensation(itemId)) return false;
        return true;
    };

    const isAnyDecisionSelected = () => {
        return Object.keys(selectedDecisions).length > 0;
    };

    const allItemsValid = () => {
        const itemsWithSelection = ret?.items.filter(item => selectedDecisions[item.item_id]);
        if (itemsWithSelection.length === 0) return false;
        return itemsWithSelection.every(item => isFormValid(item.item_id));
    };

    function handleDecisionSelect(itemId, code) {
        const next = selectedDecisions[itemId] === code ? null : code;
        if (!next) {
            const newSelected = { ...selectedDecisions };
            delete newSelected[itemId];
            setSelectedDecisions(newSelected);
            const newComments = { ...comments };
            delete newComments[itemId];
            setComments(newComments);
            const newReasons = { ...selectedReasons };
            delete newReasons[itemId];
            setSelectedReasons(newReasons);
            const newCompensation = { ...compensationAmounts };
            delete newCompensation[itemId];
            setCompensationAmounts(newCompensation);
        } else {
            setSelectedDecisions(prev => ({ ...prev, [itemId]: code }));
        }
    }

    const submitItemDecision = (itemId, abortController = new AbortController()) => {
        const selected = selectedDecisions[itemId];
        if (!selected) return;

        const decisionData = {
            code: selected,
            confirmed: true,
            comment: getHasComment(itemId) ? (comments[itemId] || "") : "",
            reason: getHasReason(itemId) ? (selectedReasons[itemId] || "") : "",
            partial_compensation: getHasCompensation(itemId) 
                ? (compensationAmounts[itemId] ? parseFloat(compensationAmounts[itemId]) : null)
                : null
        };

        const body = JSON.stringify({
            mp_id: ret?.mp_id,
            api_id: ret?.api_id,
            return_id: ret?.return_id,
            items: [{
                item_id: itemId,
                decision: decisionData
            }]
        });

        let url = `https://apps2.ecomru.ru:4655/v1/returns/add-decisions`;

        fetch(url, { body, method: 'POST', headers, signal: abortController.signal })
            .then(handleTableResponse)
            .then((json) => setSubmittedItems(prev => ({ ...prev, [itemId]: true })))
            .catch(handleRequestError)
            .finally(() => {
                if (abortController.signal.aborted) return
            })
    };

    const fetchAddDecisions = (abortController = new AbortController()) => {
        const itemsWithDecisions = ret?.items
            .map(item => {
                const itemId = item.item_id;
                const selected = selectedDecisions[itemId];
                if (!selected) return null;

                const decisionData = {
                    code: selected,
                    confirmed: true,
                    comment: getHasComment(itemId) ? (comments[itemId] || "") : "",
                    reason: getHasReason(itemId) ? (selectedReasons[itemId] || "") : "",
                    partial_compensation: getHasCompensation(itemId) 
                        ? (compensationAmounts[itemId] ? parseFloat(compensationAmounts[itemId]) : null)
                        : null
                };

                return {
                    item_id: itemId,
                    decision: decisionData
                };
            })
            .filter(Boolean);

        if (itemsWithDecisions.length === 0) return;

        const body = JSON.stringify({
            mp_id: ret?.mp_id,
            api_id: ret?.api_id,
            return_id: ret?.return_id,
            items: itemsWithDecisions
        });

        let url = `https://apps2.ecomru.ru:4655/v1/returns/add-decisions`;

        fetch(url, { body, method: 'POST', headers, signal: abortController.signal })
            .then(handleTableResponse)
            .then((json) => {
                const submittedMap = {};
                itemsWithDecisions.forEach(item => {
                    submittedMap[item.item_id] = true;
                });
                setSubmittedItems(submittedMap);
            })
            .catch(handleRequestError)
            .finally(() => {
                if (abortController.signal.aborted) return
            })
    };

    const allImages = ret?.items?.flatMap(item =>
        item?.attributes?.images?.filter(Boolean) ?? []
    ) ?? [];

    const allVideo = ret?.items?.flatMap(item =>
        item?.attributes?.videos?.filter(Boolean) ?? []
    ) ?? [];

    return (
        <>
            <ImageViewer
                isOpen={isOpen}
                images={[...allImages, ...allVideo]}
                initialIndex={selectedImageIndex}
                onClose={(e) => setIsOpen(false)}
            />

            <div className="card-return">

                {/* ── Header ── */}
                <div className="card-return__header">
                    <div className="card-return__badges">
                        {ret?.return_type_label && (
                            <span className="card-return__badge card-return__badge--blue">
                                {ret.return_type_label}
                            </span>
                        )}
                        {ret?.status_label && (
                            <span className="card-return__badge card-return__badge--amber">
                                {ret.status_label}
                            </span>
                        )}
                        {ret?.decision_state_label && (
                            <span className={`card-return__badge card-return__badge--${decisionStateModifier}`}>
                                {ret.decision_state_label}
                            </span>
                        )}
                    </div>
                    {ret?.external_ids?.mp_return_id && (
                        <span className="card-return__return-id">
                            #{ret.external_ids.mp_return_id}
                        </span>
                    )}
                </div>

                {/* ── Items ── */}
                {ret?.items?.map((item) => {
                    const images = item?.attributes?.images?.filter(Boolean) ?? [];
                    const videos = item?.attributes?.videos?.filter(Boolean) ?? [];
                    const price = formatPrice(item?.attributes?.price);
                    const itemId = item?.item_id;
                    const selected = selectedDecisions[itemId];
                    const currentDecision = chosenDecision(itemId);
                    const isSubmitted = submittedItems[itemId] || item?.decision;

                    return (
                        <div className="card-return__item" key={item?.item_id}>

                            {/* Product row */}
                            <div className="card-return__product">
                                <a
                                    className="card-return__product-image-link"
                                    href={item?.product?.url ?? "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {item?.product?.primary_image && (
                                        <img
                                            className="card-return__product-image"
                                            src={item.product.primary_image}
                                            alt={item?.product?.name ?? ""}
                                        />
                                    )}
                                </a>

                                <div className="card-return__product-info">
                                    <a
                                        className="card-return__product-name-link"
                                        href={item?.product?.url ?? "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {item?.product?.name && (
                                            <p className="card-return__product-name">{item.product.name}</p>
                                        )}
                                    </a>

                                    <div className="card-return__product-meta">
                                        {item?.product?.brand_name && (
                                            <div className="card-return__meta-item">
                                                <span className="card-return__meta-label">Бренд</span>
                                                <span className="card-return__meta-value">{item.product.brand_name}</span>
                                            </div>
                                        )}
                                        {item?.product?.category_name && (
                                            <div className="card-return__meta-item">
                                                <span className="card-return__meta-label">Категория</span>
                                                <span className="card-return__meta-value">{item.product.category_name}</span>
                                            </div>
                                        )}
                                        <div className="card-return__meta-item">
                                            <span className="card-return__meta-label">Причина</span>
                                            <span className="card-return__meta-value card-return__meta-value--reason">
                                                {getReturnReason(item?.attributes?.return_reason)}
                                            </span>
                                        </div>
                                        {item?.count != null && (
                                            <div className="card-return__meta-item">
                                                <span className="card-return__meta-label">Кол-во</span>
                                                <span className="card-return__meta-value">{item.count} шт.</span>
                                            </div>
                                        )}
                                        {price && (
                                            <div className="card-return__meta-item">
                                                <span className="card-return__meta-label">Сумма</span>
                                                <span className="card-return__meta-value card-return__meta-value--price">{price}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Media — images & videos from WB */}
                            {(images.length > 0 || videos.length > 0) && (
                                <div className="card-return__media">
                                    <span className="card-return__media-label">Фото и видео от покупателя</span>
                                    <div className="card-return__media-list">
                                        {images.map((src, i) => {
                                            const globalIndex = allImages.findIndex(img => img === src);
                                            return (
                                                <div className="card-return__media-item"
                                                    key={i}
                                                    onClick={() => {
                                                        setSelectedImageIndex(globalIndex >= 0 ? globalIndex : i);
                                                        setIsOpen(true);
                                                    }}
                                                >
                                                    <img
                                                        src={src.startsWith("//") ? `https:${src}` : src}
                                                        alt={`Фото ${i + 1}`}
                                                        className="card-return__media-img"
                                                    />
                                                </div>
                                            );
                                        })}
                                        {videos.map((src, i) => {
                                            const globalIndex = allImages.findIndex(img => img === src);

                                            return <div key={i}
                                                onClick={() => {
                                                    setSelectedImageIndex(images?.length + (globalIndex >= 0 ? globalIndex : i));
                                                    setIsOpen(true);
                                                }}
                                                className="card-return__media-item card-return__media-item--video">
                                                <video
                                                    className="card-return__media-video"
                                                    src={src.startsWith("//") ? `https:${src}` : src}
                                                    controls
                                                    preload="metadata"
                                                />
                                            </div>
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Customer comment */}
                            {item?.customer_comment && (
                                <div className="card-return__comment">
                                    <span className="card-return__comment-label">Комментарий покупателя</span>
                                    {item.customer_comment}
                                </div>
                            )}

                            {/* Decision — already set */}
                            {item?.decision && (
                                <div className="card-return__decision-done">
                                    <div className="card-return__decision-done-body">
                                        {item.decision?.code_label && (
                                            <div className="card-return__decision-done-title">
                                                {item.decision.code_label}
                                            </div>
                                        )}
                                        <div className="card-return__decision-done-meta">
                                            {item.decision?.source_label && item.decision.source_label}
                                            {item.decision?.confirmed && (
                                                <span className="card-return__badge card-return__badge--success card-return__badge--sm">
                                                    Подтверждено
                                                </span>
                                            )}
                                        </div>
                                        {item.decision?.created_at && (
                                            <div className="card-return__decision-done-date">
                                                {formatDate(item.decision.created_at)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Decision — needs selection */}
                            {!item?.decision && needsDecision && !isSubmitted && (
                                <div className="card-return__decision-select">
                                    <div className="card-return__decision-select-title">Выберите решение</div>

                                    <div className="card-return__decision-options">
                                        {availableDecisions.map((d) => (
                                            <button
                                                key={d?.code}
                                                className={[
                                                    "card-return__decision-option",
                                                    selected === d?.code ? "card-return__decision-option--active" : "",
                                                ].join(" ")}
                                                onClick={() => handleDecisionSelect(itemId, d?.code)}
                                            >
                                                <span>{d?.code_label}</span>
                                                <div className="card-return__decision-option-right">
                                                    {d?.comment_params?.is_required && (
                                                        <span className="card-return__badge card-return__badge--amber card-return__badge--sm">
                                                            Нужен комментарий
                                                        </span>
                                                    )}
                                                    {selected === d?.code && (
                                                        <span className="card-return__decision-check">✓</span>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Comment block */}
                                    {getHasComment(itemId) && currentDecision?.comment_params && (
                                        <div className="card-return__comment-block">
                                            {getCommentTemplates(itemId).length > 0 && (
                                                <div className="card-return__templates">
                                                    <span className="card-return__templates-label">Шаблоны ответов</span>
                                                    <div className="card-return__templates-list">
                                                        {getCommentTemplates(itemId).map((tpl, i) => (
                                                            <button
                                                                key={i}
                                                                className={[
                                                                    "card-return__template-btn",
                                                                    comments[itemId] === tpl.value ? "card-return__template-btn--active" : "",
                                                                ].join(" ")}
                                                                onClick={() => handleTemplateSelect(itemId, tpl.value)}
                                                            >
                                                                {tpl.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <textarea
                                                className="card-return__comment-input"
                                                value={comments[itemId] || ""}
                                                onChange={(e) => setComments(prev => ({ ...prev, [itemId]: e.target.value }))}
                                                placeholder={
                                                    getIsCommentRequired(itemId)
                                                        ? "Укажите причину (обязательно)..."
                                                        : "Комментарий (необязательно)..."
                                                }
                                            />

                                            {currentDecision.comment_params.description && (
                                                <span className="card-return__comment-hint">
                                                    {currentDecision.comment_params.description}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Reason block */}
                                    {getHasReason(itemId) && getAvailableReasons(itemId).length > 0 && (
                                        <div className="card-return__reason-block">
                                            <div className="card-return__reason-select-title">
                                                Выберите причину {getIsReasonRequired(itemId) && <span className="card-return__required-star">*</span>}
                                            </div>
                                            <div className="card-return__reason-options">
                                                {getAvailableReasons(itemId).map((reason, i) => (
                                                    <button
                                                        key={i}
                                                        className={[
                                                            "card-return__reason-option",
                                                            selectedReasons[itemId] === reason.value ? "card-return__reason-option--active" : "",
                                                        ].join(" ")}
                                                        onClick={() => setSelectedReasons(prev => ({ ...prev, [itemId]: reason.value }))}
                                                    >
                                                        {reason.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Compensation block */}
                                    {getHasCompensation(itemId) && (
                                        <div className="card-return__compensation-block">
                                            <div className="card-return__compensation-label">
                                                Сумма компенсации {getIsCompensationRequired(itemId) && <span className="card-return__required-star">*</span>}
                                            </div>
                                            <input
                                                type="number"
                                                className="card-return__compensation-input"
                                                value={compensationAmounts[itemId] || ""}
                                                onChange={(e) => setCompensationAmounts(prev => ({ ...prev, [itemId]: e.target.value }))}
                                                placeholder={`Сумма в рублях${getCompensationMin(itemId) > 0 ? ` от ${getCompensationMin(itemId)}` : ""}${getCompensationMax(itemId) > 0 ? ` до ${getCompensationMax(itemId)}` : ""}`}
                                                min={getCompensationMin(itemId) || 0}
                                                max={getCompensationMax(itemId) || undefined}
                                                step={1}
                                            />
                                            {getCompensationMaxPercent(itemId) < 100 && (
                                                <span className="card-return__compensation-hint">
                                                    Максимальный процент компенсации: {getCompensationMaxPercent(itemId)}%
                                                </span>
                                            )}
                                            {!isValidCompensation(itemId) && compensationAmounts[itemId] && (
                                                <span className="card-return__error-hint">
                                                    {getCompensationMin(itemId) > 0 && compensationAmounts[itemId] < getCompensationMin(itemId) && `Минимальная сумма: ${getCompensationMin(itemId)} ₽`}
                                                    {getCompensationMax(itemId) > 0 && compensationAmounts[itemId] > getCompensationMax(itemId) && `Максимальная сумма: ${getCompensationMax(itemId)} ₽`}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {selected && (
                                        <ButtonBasic
                                            blue
                                            text='Сохранить решение'
                                            onClick={() => {
                                                if (isFormValid(itemId)) {
                                                    submitItemDecision(itemId);
                                                }
                                            }}
                                            disabled={!isFormValid(itemId)}
                                        />
                                    )}
                                </div>
                            )}

                            {/* Submitted confirmation for this item */}
                            {isSubmitted && selected && (
                                <div className="card-return__submitted">
                                    Решение сохранено:{" "}
                                    {availableDecisions.find((d) => d?.code === selected)?.code_label}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Global save button */}
                {isAnyDecisionSelected() && Object.keys(submittedItems).length < Object.keys(selectedDecisions).length && (
                    <div className="card-return__global-submit">
                        <ButtonBasic
                            blue
                            text='Сохранить все решения'
                            onClick={() => {
                                if (allItemsValid()) {
                                    fetchAddDecisions();
                                }
                            }}
                            disabled={!allItemsValid()}
                        />
                    </div>
                )}

                {/* ── Footer ── */}
                <div className="card-return__footer">
                    {ret?.external_ids?.order_id && (
                        <span className="card-return__footer-item">
                            Заказ: <b>{ret.external_ids.order_id}</b>
                        </span>
                    )}
                    {ret?.created_at && (
                        <span className="card-return__footer-item">
                            Создан: <b>{formatDate(ret.created_at)}</b>
                        </span>
                    )}
                    {ret?.updated_at && (
                        <span className="card-return__footer-item">
                            Обновлён: <b>{formatDate(ret.updated_at)}</b>
                        </span>
                    )}
                    {ret?.external_ids?.campaign_id && (
                        <span className="card-return__footer-item">
                            Кампания: <b>{ret.external_ids.campaign_id}</b>
                        </span>
                    )}
                </div>
            </div>
        </>
    );
}